import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {TranslatePipe} from '@ngx-translate/core';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow,
  MatRowDef,
  MatTable
} from '@angular/material/table';
import {MatButton, MatIconButton} from '@angular/material/button';
import {MatIcon} from '@angular/material/icon';
import {MatPaginator, MatPaginatorIntl, PageEvent} from '@angular/material/paginator';
import {ModalComponent} from '../../../shared/components/modal/modal.component';
import {MatPaginatorI18nService} from '../../../services/material/mat-paginator-i18n.service';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {MatError, MatFormField, MatHint, MatInput, MatLabel} from '@angular/material/input';
import {MessageService} from '../../../shared/components/message.service';
import {requestProblemOf, RequestsService} from '../../../services/requests.service';
import {
  hasForbiddenCharacters,
  isBilingualRequestType,
  isFilled,
  PagedResponse,
  RequestLanguage,
  requestNameParts,
  Requests,
  RequestsFactory,
  RequestType
} from '../../../shared/model/request.model';
import {RequestDialogComponent} from '../../../shared/components/request-dialog/request-dialog.component';
import {INPUT_LIMITS} from '../../../shared/constants/input-limits';
import {HttpErrorResponse} from '@angular/common/http';
import {NgTemplateOutlet} from '@angular/common';
import {CiSelectComponent} from '../../../../profiler-theme';
import {catchError, of} from 'rxjs';
import {FaehigkeitenService} from '../../../services/faehigkeiten.service';
import {ISkillCategory} from '../../../shared/model/skill-management.model';

interface RowState {
  de: (string | null)[];
  en: (string | null)[];
}

interface EditField {
  key: 'name' | 'nameEn' | 'kategorie' | 'kategorieEn' | 'faehigkeit' | 'faehigkeitEn';
  label: string;
  language: RequestLanguage;
}

/** Die Kategorie kommt aus der festen Liste (NP20-1186); frei eingegeben werden nur die beiden Namen. */
const SKILL_ACCEPT_FIELDS: EditField[] = [
  {key: 'faehigkeit', label: 'request.modals.fieldIn.skill', language: 'de'},
  {key: 'faehigkeitEn', label: 'request.modals.fieldIn.skill', language: 'en'},
];
import {AdminService} from '../../../services/admin.service';

@Component({
  selector: 'app-request-table',
  imports: [
    TranslatePipe,
    MatTable,
    MatColumnDef,
    MatHeaderCell,
    MatCell,
    MatCellDef,
    MatHeaderCellDef,
    MatHeaderRow,
    MatHeaderRowDef,
    MatRow,
    MatRowDef,
    MatButton,
    MatIcon,
    MatIconButton,
    MatPaginator,
    ModalComponent,
    FormsModule,
    MatError,
    MatFormField,
    MatInput,
    MatLabel,
    MatHint,
    ReactiveFormsModule,
    RequestDialogComponent,
    NgTemplateOutlet,
    CiSelectComponent
  ],
  templateUrl: './request-table.component.html',
  styleUrl: './request-table.component.scss',
  providers: [{provide: MatPaginatorIntl, useClass: MatPaginatorI18nService}],
  standalone: true
})
export class RequestTableComponent implements OnInit {
  @Input() buttonLabel: string;
  @Input() tableLabel: string;
  @Input() modalEditHeader: string;
  @Input() modalDeleteLabel: string;
  @Input() modalDeleteHeader: string;
  @Input() type: RequestType;
  @Input() modalAcceptHeader: string;
  @Input() modalAcceptLabel: string;

  @Output() requestsAmount = new EventEmitter<number>();

  requests: Requests[];
  displayedColumns: string[] = ['name', 'requestedBy', 'actions'];
  bilingual = false;
  acceptFields: EditField[] = [];
  /** Bei der Fähigkeit steht in jeder Zelle „Kategorie – Fähigkeit", nicht nur der Reitername. */
  nameColumnLabel: string;
  private rowState = new Map<string, RowState>();
  protected readonly languages: RequestLanguage[] = ['de', 'en'];
  protected get LIMITS() { return INPUT_LIMITS; }
  pageIndex: number = 0;
  pageSize: number = 10;
  totalRequests: number = 0;
  currentElement: Requests;
  showDeleteModal: boolean = false;
  showEditModal: boolean = false;
  editRequestFormInvalid: boolean = true;
  showAddModal: boolean = false;
  addRequestFormInvalid: boolean = true;
  showAcceptModal: boolean = false;
  acceptFormInvalid: boolean = true;
  /** Stand beim Öffnen bzw. nach dem letzten Speichern; nur bei Abweichung wird vor der Freigabe gespeichert. */
  private acceptBaseline: Requests | null = null;
  categories: ISkillCategory[] = [];

  constructor(
    private messageService: MessageService,
    private requestService: RequestsService,
    private userService: AdminService,
    private skillService: FaehigkeitenService
  ) {
  }

  ngOnInit() {
    this.bilingual = isBilingualRequestType(this.type);
    this.nameColumnLabel = this.type == RequestType.SKILL ? 'request.table.categoryAndSkill' : this.tableLabel;
    if (this.bilingual) {
      this.displayedColumns = ['nameDe', 'nameEn', 'requestedBy', 'actions'];
      const label = this.type == RequestType.INDUSTRY ? 'request.modals.fieldIn.industry' : 'request.modals.fieldIn.mainFocus';
      this.acceptFields = this.type == RequestType.SKILL ? SKILL_ACCEPT_FIELDS : [
        {key: 'name', label, language: 'de'},
        {key: 'nameEn', label, language: 'en'},
      ];
    }
    this.loadAllRequests();
  }

  loadAllRequests() {
    this.requestService
      .getRequests(this.pageIndex, this.pageSize, this.type, false)
      .subscribe({
        next: (value: PagedResponse<Requests>) => {
          this.setRequests(value.content);
          const userIds = [...new Set(this.requests.map(r => r.requestedBy).filter(Boolean))];

          if (userIds.length > 0) {
            this.userService.getUsersByIds(userIds).subscribe(users => {
              const userMap = new Map(users.map(u => [u.id, u]));
              this.setRequests(this.requests.map(req => ({
                ...req,
                requestedByUser: req.requestedBy ? userMap.get(req.requestedBy) : null
              })));
            });
          }

          this.totalRequests = value.page.totalElements;
          this.requestsAmount.emit(this.totalRequests);
        },
        error: () => {
          this.messageService.error('request.modals.loadingError');
        }
      });
  }

  onPageChange(event: PageEvent) {
    this.pageSize = event.pageSize;
    this.pageIndex = event.pageIndex;
    this.loadAllRequests();
  }

  openDeleteModal(element: Requests) {
    this.currentElement = {...element};
    this.showDeleteModal = true;
  }

  deleteRequest(currentElement: Requests) {
    this.requestService.deleteRequest(this.type, currentElement).subscribe({
      next: () => {
        this.messageService.success('request.modals.successDeleting');
        this.loadAllRequests();
      },
      error: () => this.messageService.error('request.modals.errorDeleting')
    });
    this.currentElement = null;
    this.showDeleteModal = false;
  }

  hasChanges() {
    if (!this.currentElement) return false;
    let idx = this.requests.findIndex((u) => u.id === this.currentElement.id);
    const originalElements = [...this.requests];
    return (
      JSON.stringify(this.currentElement) !==
      JSON.stringify(originalElements[idx])
    );
  }

  editRequest(element: Requests) {
    this.currentElement = {...element};
    this.onEditRequestFormChange();
    this.showEditModal = true;
  }

  saveEditRequest(element: Requests) {
    if (!('id' in element)) return;
    let idx = this.requests.findIndex((u) => u.id === element.id);
    const newRequests = [...this.requests];
    newRequests[idx] = {...element};
    this.setRequests(newRequests);
    this.currentElement = null;

    this.requestService.updateRequest(this.type, element).subscribe({
      next: () => {
        this.messageService.success('request.modals.successEditing');
        this.loadAllRequests();
      },
      error: err => {
        this.showProblem(err, 'request.modals.errorEditing');
        this.loadAllRequests();
      }
    });
  }

  onEditRequestFormChange() {
    this.editRequestFormInvalid = !this.currentElement?.name?.trim();
  }

  get acceptHasForbiddenCharacters(): boolean {
    return this.acceptFields.some(field => hasForbiddenCharacters(this.currentElement?.[field.key]));
  }

  /** The selectable list entry the request's category matches, or null for a free-text category from before NP20-1186. */
  get acceptCategory(): ISkillCategory | null {
    const category = this.currentCategory;
    return category && this.isSelectableCategory(category) ? category : null;
  }

  /** Nur Kategorien mit englischem Namen, weil beide Sprachen gebraucht werden; die bisherige bleibt sichtbar. */
  get acceptCategoryOptions(): ISkillCategory[] {
    const current = this.currentCategory;
    return this.categories.filter(
      (category) => this.isSelectableCategory(category) || category === current,
    );
  }

  get acceptCategoryWithoutEnglish(): boolean {
    const current = this.currentCategory;
    return !!current && !this.isSelectableCategory(current);
  }

  isSelectableCategory(category: ISkillCategory): boolean {
    return isFilled(category.kategorieEn);
  }

  readonly isCategoryDisabled = (category: ISkillCategory): boolean => !this.isSelectableCategory(category);

  readonly categoryLabel = (category: ISkillCategory): string =>
    isFilled(category.kategorieEn) ? `${category.kategorie} · ${category.kategorieEn}` : category.kategorie;

  get currentCategory(): ISkillCategory | null {
    const kategorie = this.currentElement?.kategorie?.trim().toLowerCase();
    return this.categories.find(category => category.kategorie.trim().toLowerCase() === kategorie) ?? null;
  }

  onAcceptCategoryChange(kategorie: string) {
    const category = this.categories.find(entry => entry.kategorie === kategorie);
    this.currentElement = {...this.currentElement, kategorie, kategorieEn: category?.kategorieEn ?? null};
    this.onAcceptFormChange();
  }

  onAcceptFormChange() {
    this.acceptFormInvalid = this.acceptHasForbiddenCharacters
      || this.acceptFields.some(field => !isFilled(this.currentElement?.[field.key]))
      || (this.type === RequestType.SKILL && !this.acceptCategory);
  }

  protected get RequestType() { return RequestType; }

  private loadCategories() {
    this.skillService
      .categories()
      .pipe(
        catchError(() => {
          this.messageService.error('request.modals.categoriesLoadingError');
          return of([] as ISkillCategory[]);
        }),
      )
      .subscribe((categories) => {
        this.categories = [...categories].sort((a, b) =>
          a.kategorie.localeCompare(b.kategorie, 'de'),
        );
        this.onAcceptFormChange();
      });
  }

  nameParts(element: Requests, language: RequestLanguage): (string | null)[] {
    return this.rowState.get(element.id)?.[language] ?? requestNameParts(element, this.type, language);
  }

  private setRequests(requests: Requests[]) {
    this.requests = requests;
    this.rowState = new Map(requests.map(r => [r.id, {
      de: requestNameParts(r, this.type, 'de'),
      en: requestNameParts(r, this.type, 'en'),
    }]));
  }

  displayName(element: Requests, language: RequestLanguage): string {
    return this.nameParts(element, language).filter(isFilled).join(' – ');
  }

  acceptRequest(element: Requests) {
    this.requestService.acceptRequest(this.type, element).subscribe({
      next: () => {
        this.messageService.success('request.table.successAccepting');
        this.loadAllRequests();
      },
      error: err => this.showProblem(err, 'request.table.errorAccepting')
    });
  }

  openAddRequest() {
    this.showAddModal = true;
    this.currentElement = RequestsFactory.empty();
  }

  openAcceptRequest(element: Requests) {
    this.currentElement = {...element};
    if (this.bilingual) {
      this.acceptBaseline = {...element};
      if (this.type === RequestType.SKILL) this.loadCategories();
      this.onAcceptFormChange();
    }
    this.showAcceptModal = true;
  }

  saveAndAccept() {
    if (this.acceptFormInvalid) return;
    const element = {...this.currentElement};
    if (!this.acceptChanged(element)) {
      this.approve(element);
      return;
    }
    this.requestService.updateRequest(this.type, element).subscribe({
      next: () => {
        this.acceptBaseline = {...element};
        this.approve(element);
      },
      error: err => {
        this.showProblem(err, 'request.modals.errorEditing');
        this.keepAcceptOpen(element);
      }
    });
  }

  private approve(element: Requests) {
    this.requestService.acceptRequest(this.type, element).subscribe({
      next: () => {
        this.messageService.success('request.table.successAccepting');
        this.acceptBaseline = null;
        this.currentElement = null;
        this.showAcceptModal = false;
        this.loadAllRequests();
      },
      error: err => {
        this.showProblem(err, 'request.table.errorAccepting');
        this.keepAcceptOpen(element);
      }
    });
  }

  private acceptChanged(element: Requests): boolean {
    const keys = [...this.acceptFields.map(field => field.key), 'kategorie' as const];
    return keys.some(key => (element[key] ?? null) !== (this.acceptBaseline?.[key] ?? null));
  }

  private keepAcceptOpen(element: Requests) {
    this.currentElement = element;
    this.showAcceptModal = true;
    this.loadAllRequests();
  }

  onAddRequestFormChange() {
    this.addRequestFormInvalid = !this.currentElement?.name?.trim();
  }

  saveAddRequest(element: Requests) {
    this.requestService.newRequest(this.type, element).subscribe({
      next: () => {
        this.messageService.success('request.modals.successEditing');
        this.currentElement = null;
        this.loadAllRequests();
        this.showAddModal = false;
      },
      error: (err) => {
        if (err.status == 409) {
          this.messageService.error('request.modals.alreadyExists');
        } else {
          this.messageService.error('request.modals.errorEditing');
        }
        this.showAddModal = true;
      }
    });
  }

  private showProblem(err: HttpErrorResponse, fallbackKey: string) {
    switch (requestProblemOf(err)) {
      case 'bothLanguagesRequired':
        this.messageService.error('request.modals.translationMissing');
        return;
      case 'alreadyExists': {
        const detail = this.conflictingName(err);
        if (detail) {
          this.messageService.errorWithDetail('request.modals.nameTaken', detail);
        } else {
          this.messageService.error('request.modals.nameTaken');
        }
        return;
      }
      default:
        this.messageService.error(fallbackKey);
    }
  }

  private conflictingName(err: HttpErrorResponse): string {
    const p: Record<string, string> = err?.error?.parameters ?? {};
    const de = this.type == RequestType.SKILL ? [p['conflictingKategorie'], p['conflictingFaehigkeit']] : [p['conflictingName']];
    const en = this.type == RequestType.SKILL ? [p['conflictingKategorieEn'], p['conflictingFaehigkeitEn']] : [p['conflictingNameEn']];
    return [de, en]
      .map(parts => parts.filter(isFilled).join(' – '))
      .filter(isFilled)
      .join(' / ');
  }
}
